import { Directive, ElementRef, forwardRef, HostListener, inject, input, Renderer2 } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, Validator } from '@angular/forms';
import {
  formatDecimal0DigitsWithoutGrouping,
  formatDecimal2DigitsWithoutGrouping,
  isDefined
} from '@mng/mng-ts-helpers';
import { IMngNumberFormat } from '../input.types';

function keyFor(allowNegative: boolean, allowDecimals: boolean): string {
  return `${allowNegative}_${allowDecimals}`;
}

export interface INumberPattern {
  pattern: RegExp;
  patternAsString: string;
}

function createPattern(pattern: RegExp, patternAsString): INumberPattern {
  return { pattern, patternAsString };
}

const numberPatternWithDecimals = /^[0-9]*(,\d{1,2})?$/;
const numberPatternWithMinusAndDecimals = /^(-)?[0-9]*(,\d{1,2})?$/;
const numberPatternWithMinus = /^(-)?[0-9]*$/;
const numberPattern = /^[0-9]*$/;

export const numberPatterns: { [key: string]: INumberPattern } = {
  [keyFor(true, true)]: createPattern(numberPatternWithMinusAndDecimals, '(-)99999(,99)'),
  [keyFor(false, true)]: createPattern(numberPatternWithDecimals, '99999(,99)'),
  [keyFor(true, false)]: createPattern(numberPatternWithMinus, '(-)99999'),
  [keyFor(false, false)]: createPattern(numberPattern, '99999')
};

export const NUMBER_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => NumberFieldDirective),
  multi: true
};

export const NUMBER_VALUE_VALIDATOR: any = {
  provide: NG_VALIDATORS,
  useExisting: forwardRef(() => NumberFieldDirective),
  multi: true
};

/**
 * Dient zur Umwandlung und Validierung von numerischen Werten die ein input field mit type="text" eingegeben werden
 *
 *  ### Example
 *   `<input mngNumberField type="text" [formControl]="modelItem.control">`
 **/
@Directive({
  selector: '[mngNumberField]',

  providers: [NUMBER_VALUE_ACCESSOR, NUMBER_VALUE_VALIDATOR]
})
export class NumberFieldDirective implements ControlValueAccessor, Validator {
  private renderer = inject(Renderer2);
  private elementRef = inject(ElementRef);

  viewValue: string;
  value: number;
  valid: boolean;

  mngNumberField = input<IMngNumberFormat>({
    allowNegativeNumber: false,
    allowDecimals: false
  });

  @HostListener('change', ['$event.target.value'])
  onChange = (value: any) => {
    return;
  };

  @HostListener('input', ['$event.target.value'])
  onInput = this.onChange;

  onTouched = () => {
    return;
  };

  @HostListener('blur', [])
  onBlur = () => {
    if (this.valid && isDefined(this.value) && this.mngNumberField()?.allowDecimals) {
      // Eingabewert formatieren
      try {
        this.viewValue = formatDecimal2DigitsWithoutGrouping(this.value);
        this.renderer.setProperty(this.elementRef.nativeElement, 'value', this.viewValue);
      } catch (e) {
        console.error(e);
      }
    }
    this.onTouched();
  };

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent) {
    const clipboardData = event.clipboardData || window['clipboardData'];
    const pastedValue = clipboardData.getData('text');
    if (!this.isValidNumberString(pastedValue)) {
      console.error('Pasted value ', pastedValue, ' is not valid');
      event.preventDefault();
    }
  }

  /**
   * Wert --> HTML Element
   * @param value
   */
  writeValue(value: string | number) {
    // MODEL -> VIEW
    // console.log('writeValue', value);

    this.viewValue = '';
    if (isDefined(value)) {
      if (typeof value === 'number') {
        this.value = value;
        if (this.mngNumberField()?.allowDecimals) {
          // Wert 2 mit Kommastellen anzeigen
          this.viewValue = formatDecimal2DigitsWithoutGrouping(value);
        } else {
          this.viewValue = formatDecimal0DigitsWithoutGrouping(value);
        }
      } else {
        this.viewValue = value.replaceAll('.', ',');
        this.value = Number.parseFloat(value);
      }
    }

    this.renderer.setProperty(this.elementRef.nativeElement, 'value', this.viewValue);
  }

  /**
   *
   * @param fn
   */
  registerOnChange(fn: (_: any) => void) {
    // VIEW -> MODEL
    this.onChange = (value) => {
      this.viewValue = value;

      const numberValue = value === '' ? null : +value.toString().replaceAll(',', '.');

      this.value = numberValue;
      // console.log('onChange: view -> model', numberValue);
      fn(numberValue);
    };
    this.onInput = this.onChange;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.renderer.setProperty(this.elementRef.nativeElement, 'disabled', isDisabled);
  }

  getNumberPattern(): INumberPattern {
    return numberPatterns[keyFor(this.mngNumberField().allowNegativeNumber, this.mngNumberField().allowDecimals)];
  }

  isValidNumberString(value: string): boolean {
    // console.log('isValidNumberString', value, this.getNumberPattern(), this.getNumberPattern().pattern.test(value));
    return this.getNumberPattern().pattern.test(value);
  }

  validate(c: AbstractControl): { [key: string]: any } {
    const validFormat = this.isValidNumberString(this.viewValue);
    const result = !validFormat
      ? { [`Zahl ist im falschen Format: ${this.getNumberPattern().patternAsString}`]: true }
      : null;

    this.valid = result === null;
    // console.log('validate -> this.viewValue', this.viewValue, 'control.value', c.value, 'result', result);
    return result;
  }
}
